import { Link, Redirect, useLocalSearchParams, useRouter } from "expo-router";
import { Sparkles } from "lucide-react-native";
import { StyleSheet, Text, View } from "react-native";

import { PageShell, SectionIntro } from "../src/components/layout";
import { CentralAuthShell } from "../src/components/CentralAuthShell";
import { Button, Panel } from "../src/components/ui";
import { StaffSignInPanel } from "../src/components/StaffSignInPanel";
import { makeSlug } from "../src/lib/format";
import { cleanStaffPath, isCentralHost } from "../src/lib/hosting";
import { useAppStore } from "../src/providers/AppProvider";
import { useStaffAuth } from "../src/providers/StaffAuthProvider";
import { palette, spacing } from "../src/theme/tokens";

export default function SignedOutScreen() {
  const router = useRouter();
  const { returnTo } = useLocalSearchParams<{ returnTo?: string | string[] }>();
  const { ready, staffSessionActive, data } = useAppStore();
  const staffAuth = useStaffAuth();
  const centralHost = isCentralHost();
  const safeReturnTo = getSafeReturnTo(returnTo, centralHost);

  if (!ready) {
    return null;
  }

  if (!centralHost && !data.onboardingComplete) {
    return <Redirect href="/onboarding" />;
  }

  if (staffSessionActive) {
    return (
      <Redirect
        href={
          centralHost && staffAuth.identity?.access.role === "support"
            ? "/workspace/family-access-support"
            : safeReturnTo
        }
      />
    );
  }

  if (centralHost) {
    return (
      <CentralAuthShell>
        <StaffSignInPanel />
      </CentralAuthShell>
    );
  }

  const firstMemorial = data.memorials[0];
  const slug = firstMemorial ? makeSlug(firstMemorial.decedentName) : "jennifer-miano";

  return (
    <PageShell role="public">
      <View style={styles.content}>
        <SectionIntro
          eyebrow="Signed out"
          title="Signed out on this device"
          description="Sign in with your staff account to return to Central."
        />

        <StaffSignInPanel />

        <Panel style={styles.secondaryCard}>
          <View style={styles.header}>
            <Sparkles color={palette.gold} size={18} strokeWidth={1.8} />
            <Text style={styles.title}>Public Memorial Preview</Text>
          </View>
          <Text style={styles.body}>
            Keep showcasing the guest experience without opening the internal app shell.
          </Text>
          <Link href={`/memorial/${slug}`} asChild>
            <Button label="Open Guest Page" variant="ghost" />
          </Link>
        </Panel>
      </View>
    </PageShell>
  );
}

function getSafeReturnTo(returnTo: string | string[] | undefined, centralHost: boolean) {
  const fallback = centralHost ? "/" : "/admin";
  const candidate = Array.isArray(returnTo) ? returnTo[0] : returnTo;
  if (!candidate || !candidate.startsWith("/")) return fallback;
  if (candidate.startsWith("//")) return fallback;
  if (candidate.startsWith("/signed-out")) return fallback;

  return centralHost ? cleanStaffPath(candidate) : candidate;
}

const styles = StyleSheet.create({
  content: {
    gap: spacing.xl,
    paddingBottom: spacing.xxl,
  },
  secondaryCard: {
    gap: 14,
  },
  header: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  title: {
    color: palette.ink,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 28,
    lineHeight: 30,
    textTransform: "capitalize",
  },
  body: {
    color: palette.inkSoft,
    fontFamily: "Manrope_400Regular",
    fontSize: 14,
    lineHeight: 22,
  },
});
